import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  courses = [],
  isForceDelete = false,
  loading = false
}) => {
  const { t } = useTranslation(['common', 'modules']);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="div">
            {isForceDelete ? t('common:deleteConfirmation.forceDeleteTitle') : t('common:deleteConfirmation.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            {title}
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {message}
        </Typography>

        {courses.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
              {t('modules:deleteConfirmation.usedInCourses')}
            </Typography>
            <List dense sx={{ bgcolor: 'error.50', borderRadius: 1, py: 0 }}>
              {courses.map((course, index) => (
                <React.Fragment key={course.id}>
                  <ListItem sx={{ py: 1 }}>
                    <ListItemText
                      primary={course.title}
                      secondary={course.description}
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.8rem' }}
                    />
                  </ListItem>
                  {index < courses.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {isForceDelete && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t('common:deleteConfirmation.warning')}</strong> {t('modules:deleteConfirmation.forceDeleteMessage')}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          {t('common:cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={loading}
        >
          {loading ? t('common:deleting') : t('common:delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
